/**
 * Shopping Preferences Default Values
 *
 * Pure functions for creating default preference objects.
 */

import { ShoppingPreferences } from '@lib/shopping-preferences/types';

/**
 * Get current ISO timestamp
 */
export const nowISO = (): string => new Date().toISOString();

/**
 * Create default shopping preferences
 * No universe set by default - user must select
 */
export const createDefaultPreferences = (): ShoppingPreferences => ({
  universe: undefined,
  categories: [],
  custom: [],
  last_updated: nowISO(),
});

/**
 * Create preferences with timestamp
 */
export const withTimestamp = (preferences: Partial<ShoppingPreferences>): ShoppingPreferences => ({
  universe: preferences.universe,
  categories: preferences.categories ?? [],
  custom: preferences.custom ?? [],
  last_updated: nowISO(),
});

/**
 * Merge preferences with defaults
 */
export const mergeWithDefaults = (preferences: Partial<ShoppingPreferences> | null): ShoppingPreferences => {
  if (!preferences) {
    return createDefaultPreferences();
  }

  return {
    universe: preferences.universe,
    categories: preferences.categories,
    custom: preferences.custom,
    last_updated: preferences.last_updated ?? nowISO(),
  };
};

/**
 * Check if preferences are defaults (no user selection)
 */
export const areDefaultPreferences = (preferences: ShoppingPreferences | null): boolean => {
  if (!preferences) {
    return true;
  }

  return (
    (preferences.universe === undefined || preferences.universe === null) &&
    (!preferences.categories || preferences.categories.length === 0) &&
    (!preferences.custom || preferences.custom.length === 0)
  );
};
